import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ChevronRight, Zap, ShieldCheck, Users, Wallet, FileText, CalendarCheck } from "lucide-react";
import { Brand } from "@/components/Brand";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

const stats = [];

const features = [
  { icon: Users, title: "Employees & departments", body: "One workspace per client. CRUD employees with salary structures, departments, designations, and auto-generated login accounts." },
  { icon: CalendarCheck, title: "Attendance you can trust", body: "Employees clock in/out from their portal. HR sees the whole month in a strict, high-density grid — no dashboards, just data." },
  { icon: Wallet, title: "One-click payroll run", body: "Generate every payslip for a month with a single action. Basic + HRA + allowances − deductions − tax, all itemised." },
  { icon: FileText, title: "Downloadable payslips", body: "Employees log in to view and print their payslips. Every line item is preserved for compliance." },
  { icon: ShieldCheck, title: "Role-based access", body: "Service provider, HR, employee — three tiers of access enforced end-to-end, per client company." },
  { icon: Zap, title: "Sell it as a service", body: "Onboard client companies from your admin console. Your name, your pricing, your relationship." },
];

const tiers = [
  { name: "Starter", price: "$49", per: "per client · month", features: ["Up to 25 employees", "Attendance + Payslips", "Email support"], cta: "Start free" },
  { name: "Growth", price: "$149", per: "per client · month", featured: true, features: ["Up to 250 employees", "Bulk payroll runs", "Custom salary structures", "Priority support"], cta: "Book a demo" },
  { name: "Firm", price: "Custom", per: "unlimited seats", features: ["Multiple client companies", "White-label options", "Dedicated success manager"], cta: "Contact sales" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAF7EF] text-neutral-950">
      <SiteNav />

      {/* Hero */}
      <section className="border-b border-[#E8DFC5] bg-grid relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(1000px 500px at 80% -10%, rgba(201,169,97,0.18), transparent 60%)"}}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 relative">
          <div className="max-w-4xl">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-6 flex items-center gap-3" data-testid="hero-eyebrow">
              <span className="inline-block w-8 h-px bg-[#C9A961]"></span>
              A premium payroll servicing partner
              <span className="text-[#C9A961]">★</span>
            </div>
            <h1 className="font-serif font-black text-[54px] leading-[0.95] md:text-[92px] tracking-[-0.035em]" data-testid="hero-title">
              White-glove payroll,<br />
              <span className="italic text-gold-gradient">issued in your name.</span>
            </h1>
            <p className="mt-8 text-lg text-neutral-700 max-w-2xl leading-relaxed">
              HG Consultancy runs the entire payroll cycle for your business — attendance, statutory compliance,
              itemised payslips, and Form-16-ready records. All under one licensed roof, with a dedicated relationship manager.
            </p>
            <div className="mt-12 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-primary" className="group inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-7 py-4 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
                Open a client workspace
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-200" />
              </Link>
              <Link to="/login" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 border border-[#C9A961] text-neutral-950 px-7 py-4 font-mono text-sm hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors">
                Client sign in
              </Link>
              <a href="#contact" data-testid="hero-cta-contact" className="inline-flex items-center gap-2 border border-transparent px-7 py-4 font-mono text-sm text-[#8C6F1E] hover:text-neutral-950 transition-colors">
                Speak to a partner →
              </a>
            </div>

            {/* Trust row */}
            <div className="mt-16 pt-8 border-t border-[#E8DFC5] flex flex-wrap items-center gap-x-10 gap-y-3 text-[11px] font-mono uppercase tracking-widest text-neutral-500">
              <span className="text-gold-deep">Licensed · Est. Harguaon Group</span>
              <span>·</span>
              <span>GSTIN 06ATUPP9372R1Z9</span>
              <span>·</span>
              <span>India · FY 2025-26 compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features — Bento */}
      <section id="features" className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">§ 01 — Service</div>
              <h2 className="font-serif font-black text-5xl md:text-6xl tracking-[-0.02em] leading-[0.95]">
                A payroll office<br />in your <span className="italic text-gold-gradient">back pocket.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-lg text-neutral-700 leading-relaxed">
                Handpicked practitioners, statutory-grade software, and a service level that reads like a chartered accountant's brochure —
                minus the paperwork. Every deliverable is signed off before it reaches your team.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={f.title} className={`group border border-[#E8DFC5] bg-white p-8 hover:border-[#C9A961] hover:shadow-[0_0_0_1px_rgba(201,169,97,0.4)] transition-all duration-200 ${i === 0 ? "lg:col-span-2 lg:row-span-1" : ""}`}>
                <f.icon className="w-6 h-6 mb-6 text-[#8C6F1E]" strokeWidth={1.5} />
                <div className="font-serif font-bold text-2xl tracking-tight mb-2">{f.title}</div>
                <p className="text-sm text-neutral-600 leading-relaxed">{f.body}</p>
                <div className="mt-6 text-[10px] font-mono uppercase tracking-widest text-neutral-400 group-hover:text-[#8C6F1E] transition-colors">
                  0{i + 1} / 0{features.length}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="border-b border-[#E8DFC5] bg-neutral-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(600px 300px at 20% 100%, rgba(201,169,97,0.25), transparent 60%)"}}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4B87D] mb-3 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#C9A961]"></span>
            § 02 — Engagement
          </div>
          <h2 className="font-serif font-black text-5xl md:text-6xl tracking-[-0.02em] mb-16 max-w-3xl leading-[0.95]">
            Three appointments,<br />
            <span className="italic text-gold-gradient">a lifetime of correct payslips.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-[#2a2416]">
            {[
              { n: "I", t: "Discovery", d: "A partner reviews your headcount, salary structures and statutory footprint. We propose the engagement in writing." },
              { n: "II", t: "Onboarding", d: "Employees are added by our team from your source of truth. Every salary structure is verified against offer letters." },
              { n: "III", t: "Servicing", d: "Attendance closed, payroll run, itemised payslips issued, and statutory returns filed — every single month, without a nudge." },
            ].map((step) => (
              <div key={step.n} className="bg-neutral-950 p-10">
                <div className="font-serif italic text-[#C9A961] text-4xl mb-6">{step.n}</div>
                <div className="font-serif font-bold text-3xl tracking-tight mb-3">{step.t}</div>
                <p className="text-sm text-neutral-400 leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">§ 03 — Retainer</div>
          <h2 className="font-serif font-black text-5xl md:text-6xl tracking-[-0.02em] mb-16 max-w-3xl leading-[0.95]">
            Predictable retainers.<br />
            <span className="italic text-gold-gradient">Zero surprise invoices.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`border p-8 flex flex-col transition-all ${tier.featured ? "border-[#C9A961] bg-neutral-950 text-white shadow-[0_0_0_1px_rgba(201,169,97,0.35),0_20px_60px_-20px_rgba(201,169,97,0.35)]" : "border-[#E8DFC5] bg-white hover:border-[#C9A961]"}`}
              >
                <div className={`text-[10px] font-mono uppercase tracking-widest mb-6 ${tier.featured ? "text-[#D4B87D]" : "text-neutral-500"}`}>
                  {tier.featured ? "★ Recommended" : "Retainer"}
                </div>
                <div className="font-serif font-bold text-3xl mb-2">{tier.name}</div>
                <div className="flex items-baseline gap-2 mb-8">
                  <span className={`font-serif font-black text-5xl tracking-tighter ${tier.featured ? "text-gold-gradient" : ""}`}>{tier.price}</span>
                  <span className={`text-xs font-mono ${tier.featured ? "text-neutral-400" : "text-neutral-500"}`}>{tier.per}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.featured ? "text-[#C9A961]" : "text-[#8C6F1E]"}`} strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                  className={`inline-flex items-center justify-between gap-2 px-5 py-3.5 font-mono text-sm transition-colors ${
                    tier.featured
                      ? "bg-[#C9A961] text-neutral-950 hover:bg-white"
                      : "border border-neutral-950 hover:bg-neutral-950 hover:text-[#D4B87D]"
                  }`}
                >
                  {tier.cta}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">§ 04 — FAQ</div>
            <h2 className="font-serif font-black text-5xl tracking-[-0.02em] leading-[0.95]">Answers.<br /><span className="italic text-gold-gradient">Straight.</span></h2>
          </div>
          <div className="lg:col-span-8 divide-y divide-[#E8DFC5] border-y border-[#E8DFC5]">
            {[
              ["Is my data isolated from other clients?", "Yes. Every client company is fully isolated. Employees of one client can never see data belonging to another."],
              ["Do you white-label the payslip?", "Every payslip carries your logo, GSTIN and address by default. Our HG Consultancy mark appears only as a small footer credit."],
              ["Which Indian tax rules are supported?", "New Regime FY 2025-26 with ₹75,000 standard deduction, 87A rebate up to ₹12 lakh, and 4% cess. EPF, ESI, and state-wise Professional Tax + LWF are baked in."],
              ["How is data secured?", "HTTPS in transit, bcrypt-hashed passwords, HTTP-only cookies for sessions, and role-based access enforced server-side on every request."],
            ].map(([q, a]) => (
              <details key={q} className="group py-6">
                <summary className="cursor-pointer flex items-center justify-between font-serif font-bold text-xl tracking-tight list-none">
                  {q}
                  <span className="w-7 h-7 border border-[#C9A961] text-[#8C6F1E] flex items-center justify-center group-open:bg-[#C9A961] group-open:text-neutral-950 transition-colors">+</span>
                </summary>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed max-w-2xl">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-b border-[#E8DFC5] bg-neutral-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(700px 400px at 90% 30%, rgba(201,169,97,0.22), transparent 60%)"}}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-end relative">
          <div className="lg:col-span-7">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4B87D] mb-3 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#C9A961]"></span>
              § 05 — Concierge
            </div>
            <h2 className="font-serif font-black text-5xl md:text-6xl tracking-[-0.02em] leading-[0.95]">
              Speak to a<br />
              <span className="italic text-gold-gradient">partner directly.</span>
            </h2>
            <p className="mt-6 text-neutral-400 max-w-lg leading-relaxed">
              Every engagement begins with a private conversation about your headcount, statutory needs and timeline.
              No sales-desk hoops, no generic email queues.
            </p>
          </div>
          <div className="lg:col-span-5 space-y-4" data-testid="contact-card">
            <a href="tel:+919355653653" data-testid="contact-phone" className="group flex items-center justify-between border border-[#C9A961]/50 px-6 py-5 hover:bg-[#C9A961] hover:text-neutral-950 hover:border-[#C9A961] transition-colors">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#D4B87D] group-hover:text-neutral-700">Direct line</div>
                <div className="mt-1 font-serif font-black text-3xl tracking-tighter">+91 93556 53653</div>
              </div>
              <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-200" />
            </a>
            <a href="mailto:contact@harguonservices.com" data-testid="contact-email" className="group flex items-center justify-between border border-[#C9A961]/50 px-6 py-5 hover:bg-[#C9A961] hover:text-neutral-950 hover:border-[#C9A961] transition-colors">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#D4B87D] group-hover:text-neutral-700">Email a partner</div>
                <div className="mt-1 font-mono text-lg break-all">contact@harguonservices.com</div>
              </div>
              <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-200" />
            </a>
            <div className="pt-4 text-[11px] font-mono uppercase tracking-widest text-neutral-500">
              Mon – Sat · 10:00 – 19:00 IST
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
